import { EZuntaInvoiceStatus, EZuntaInvoiceType, IBillingBatchHistory, IBillingHistory, ICommissionsSettings, IFacilityBillingSettings, IFacilityMonthlyBilling, IFacilityMonthlyBillingBatch, IZuntaInvoice } from '@zunta/configurations'
import ApiInstance from './instance'
import { numeric } from 'vuelidate/lib/validators'

export default class BillingApi {
  instance: ApiInstance;
  constructor (instance: ApiInstance) {
    this.instance = instance
  }
  saveSettings (data: Partial<IFacilityBillingSettings>): Promise<IFacilityBillingSettings> {
    return this.instance.doPut('/billing/settings', { settings: data })
  }
  addBillingHistory (data: Partial<IBillingHistory>) {
    return this.instance.doPost('/billing/billing-history', data)
  }
  getBillingStatistics (months: number, skip: number) {
    return this.instance.doGet(`/billing/billing-statistics`, {
      months,
      skip
    })
  }
  getChainsStatistics (months: number, skip: number) {
    return this.instance.doGet('/billing/chains-statistics', {
      months,
      skip
    })
  }
  updateBatch (batchId: number | string) {
    return this.instance.doPut(`/billing/batches/update/${batchId}`)
  }
  importBatchesReport (file: File) {
    const formData = new FormData()
    formData.append('report', file)
    return this.instance.doPost(`/billing/import-batch-report`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
  
  importAccountDebit (file: File) {
    const formData = new FormData()
    formData.append('report', file)
    return this.instance.doPost(`/billing/import-debit-account`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
  getSalesRepStatistics (userId: number, months: number, skip: number) {
    return this.instance.doGet(`/billing/sales-rep-statistics/${userId}`, {
      months,
      skip
    })
  }
  getSapesRepsList () {
    return this.instance.doGet('/billing/sale-reps-list')
  }
  getFacilitiesStatistics (months: number, skip: number) {
    return this.instance.doGet('/billing/facilities-statistics', {
      months,
      skip
    })
  }
  getClientsByModulesStatistics (months: number, skip: number) {
    return this.instance.doGet(`/billing/clients-by-module-statistics`, {
      months,
      skip
    })
  }

  getBillingBatchHistory (batchId: number | string) {
    return this.instance.doGet(`/billing/billing-batch-history/${batchId}`)
  }

  addBillingBatchHistory (data: Partial<IBillingBatchHistory>) {
    return this.instance.doPost(`/billing/billing-batch-history`, data)
  }

  getRevenueByModulesStatistics (months: number, skip: number) {
    return this.instance.doGet(`/billing/revenues-by-module-statistics`, {
      months,
      skip
    })  
  }

  getFacilityBiilingSettings (facilityId: number): Promise<IFacilityBillingSettings | null> {
    return this.instance.doGet(`/billing/facility/${facilityId}`)
  }

  getBillingBatchesByFilters (filters: any): Promise<{ batches: IFacilityMonthlyBillingBatch[], total: number }> {
    return this.instance.doGet('/billing/batches-by-filters', filters, 'billing-batches')
  }
  getInvoicesByFilters (filters: any): Promise<{ invoices: IZuntaInvoice[], total: number }> {
    return this.instance.doGet('/billing/invoices-by-filters', filters, 'billing-invoices')
  }
  uploadCosts (batchId: number | string, file: File) {
    const formData = new FormData()
    formData.append('costs', file)
    return this.instance.doPost(`/billing/import-costs/${batchId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
  billInvoices (batchId: number | string) {
    return this.instance.doPost(`/billing/bill-invoices/${batchId}`)
  }
  billInvoice (invoiceId: number | string) {
    return this.instance.doPost(`/billing/bill-invoice/${invoiceId}`)  
  }
  rebillInvoice (invoiceId: number | string) {
    return this.instance.doPost(`/billing/rebill-invoice/${invoiceId}`)
  }
  refundInvoice (invoiceId: number | string, options: any) {
    return this.instance.doPost(`/billing/refund-invoice/${invoiceId}`, options)
  }
  generateMonthlyBillings (invoiceTypes: EZuntaInvoiceType[], date: Date) {
    return this.instance.doPost('/billing/generateMonthlyBillings', { invoiceTypes, date })
  }
  updateBatchInvoiceTypes (batchId: number | string, invoiceTypes: EZuntaInvoiceType[]) {
    return this.instance.doPatch(`/billing/billing-batches/invoice-types/${batchId}`, { invoiceTypes })
  }
  getBillingHistory (invoiceId: number | string) {
    return this.instance.doGet(`/billing/billing-history/${invoiceId}`)
  }
  saveCommissionsSettings (facilityId: number | string, settings: Partial<ICommissionsSettings>[]) {
    return this.instance.doPost(`/billing/commissions-settings/${facilityId}`, { settings })
  }
  getCommissionsSettings (facilityId: number | string) {
    return this.instance.doGet(`/billing/commissions-settings/${facilityId}`)
  }
  getBillingBatch (batchId: number | string) {
    return this.instance.doGet(`/billing/billing-batches/${batchId}`)
  }
  getUserBillingBatch (batchId: number | string, userId: number | string) {
    return this.instance.doGet(`/billing/billing-batches/${batchId}/users/${userId}`)
  }
  getBatchUsersCommissions (batchId: number | string) {
    return this.instance.doGet(`/billing/batch-users-commissions/${batchId}`)
  }
  getCommissionsByFilters (filters: any) {
    return this.instance.doGet(`/billing/comissions-by-filters`, filters, 'commissions-by-filters')
  }
  getFacilitiesCountStatistic (facilities: string[] | number[]) {
    return this.instance.doGet('/billing/facilities-count-statistic', { facilities })
  }
  getBillingBatchPagination (options: any) {
    return this.instance.doGet('/billing/billing-batch-pagination', options, 'billing-batch-pagination')
  }
  removeBatch (batchId: number) {
    return this.instance.doDelete(`/billing/billings/batches/${batchId}`)
  }

  regenerateInvoice (
      invoice: IZuntaInvoice
  ) {
    return this.instance.doFetch<IZuntaInvoice>(
      'billing/manual-regenerate-invoice',
      invoice,
      'PUT'
    )
  }

  getFacilityMonthlyBillingById (id: number) {
    return this.instance.doFetch<IFacilityMonthlyBilling>(
      `billing/monthlyBilling/${id}`
    )
  }

  getInvoiceById (id: number) {
    return this.instance.doFetch<IZuntaInvoice>(
     `billing/invoice/${id}`
    )
  }
}
